import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Chip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ReceiptLong as PurchaseIcon,
  AssignmentReturn as ReturnIcon,
  Payments as PaymentIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { supplierService } from '../../services/supplierService';
import purchaseOrderService from '../../services/purchaseOrderService';
import supplierCreditService from '../../services/supplierCreditService';
import { enqueueSnackbar } from 'notistack';
import { formatCurrency } from '../../utils/currencyFormatter';
import { formatDate } from '../../utils/dateFormatter';
import * as XLSX from 'xlsx';

const getTodayIso = () => new Date().toISOString().split('T')[0];
const getMonthStartIso = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
};

const safeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const toDateOnly = (value) => {
  if (!value) return null;
  if (Array.isArray(value) && value.length >= 3) {
    const y = value[0];
    const m = String(value[1]).padStart(2, '0');
    const day = String(value[2]).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  if (typeof value === 'string') return value.slice(0, 10);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().split('T')[0];
};

const getPoDate = (order) => {
  return toDateOnly(order?.orderDate) || toDateOnly(order?.createdAt);
};

const poItemCount = (order) => {
  if (Array.isArray(order?.items)) return order.items.length;
  if (Array.isArray(order?.purchaseOrderItems)) return order.purchaseOrderItems.length;
  return 0;
};

const buildActivities = (purchaseOrders, creditTransactions) => {
  const poRows = (Array.isArray(purchaseOrders) ? purchaseOrders : []).map((order) => {
    const isReturned = String(order?.status || '').toUpperCase() === 'RETURNED';
    const type = isReturned ? 'RETURN' : 'PURCHASE';
    const itemCount = poItemCount(order);
    const ref = order?.orderNumber || `PO-${order?.id || ''}`;
    const pay = order?.paymentMethod ? String(order.paymentMethod).toUpperCase() : '';

    return {
      id: `PO-${order?.id || ref}`,
      date: getPoDate(order),
      type,
      reference: ref,
      description: isReturned
        ? `Purchase return (${itemCount} items)`
        : `Purchase / goods received (${itemCount} items)${pay ? ` — ${pay}` : ''}`,
      amount: safeNumber(order?.total),
      source: order
    };
  });

  const creditRows = (Array.isArray(creditTransactions) ? creditTransactions : [])
    .filter((tx) => {
      const txType = String(tx?.transactionType || tx?.transaction_type || '').toUpperCase();
      return txType === 'PAYMENT' || txType === 'OPENING_BALANCE';
    })
    .map((tx) => {
      const txType = String(tx?.transactionType || tx?.transaction_type || '').toUpperCase();
      return {
        id: `STX-${tx?.id || tx?.referenceId || tx?.reference_id || Math.random()}`,
        date: toDateOnly(tx?.transactionDate || tx?.transaction_date || tx?.createdAt),
        type: txType === 'OPENING_BALANCE' ? 'OPENING_BALANCE' : 'PAYMENT',
        reference: tx?.referenceId || tx?.reference_id || `PAY-${tx?.id || ''}`,
        description: txType === 'OPENING_BALANCE'
          ? 'Opening payable balance'
          : `Payment to supplier${tx?.paymentMethod ? ` (${tx.paymentMethod})` : ''}`,
        amount: safeNumber(tx?.amount),
        source: tx
      };
    });

  return [...poRows, ...creditRows]
    .filter((row) => !!row.date)
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));
};

const SupplierLedgerHistoryPage = () => {
  const navigate = useNavigate();
  const { supplierId } = useParams();

  const [loading, setLoading] = useState(true);
  const [supplier, setSupplier] = useState(null);
  const [activities, setActivities] = useState([]);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [filters, setFilters] = useState({
    startDate: getMonthStartIso(),
    endDate: getTodayIso()
  });

  useEffect(() => {
    const loadData = async () => {
      if (!supplierId) return;

      try {
        setLoading(true);
        const [supplierData, purchaseOrders, creditTransactions] = await Promise.all([
          supplierService.getSupplierById(supplierId),
          purchaseOrderService.getPurchaseOrdersBySupplier(supplierId),
          supplierCreditService.getSupplierTransactions(supplierId)
        ]);

        setSupplier(supplierData);
        const poList = Array.isArray(purchaseOrders) ? purchaseOrders : [];
        const txList = Array.isArray(creditTransactions) ? creditTransactions : [];
        setActivities(buildActivities(poList, txList));
      } catch (error) {
        console.error('Failed to load supplier ledger history:', error);
        enqueueSnackbar('Failed to load supplier ledger history', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [supplierId]);

  const filteredActivities = useMemo(() => {
    const { startDate, endDate } = filters;
    return activities.filter((row) => {
      const d = row.date || '';
      if (startDate && d < startDate) return false;
      if (endDate && d > endDate) return false;
      return true;
    });
  }, [activities, filters]);

  const summary = useMemo(() => {
    return filteredActivities.reduce(
      (acc, row) => {
        if (row.type === 'PURCHASE') {
          acc.totalPurchases += row.amount;
          acc.purchaseCount += 1;
        } else if (row.type === 'RETURN') {
          acc.totalReturns += row.amount;
          acc.returnCount += 1;
        } else if (row.type === 'PAYMENT') {
          acc.totalPayments += row.amount;
          acc.paymentCount += 1;
        }
        return acc;
      },
      {
        totalPurchases: 0,
        totalReturns: 0,
        totalPayments: 0,
        purchaseCount: 0,
        returnCount: 0,
        paymentCount: 0
      }
    );
  }, [filteredActivities]);

  useEffect(() => {
    setPage(0);
  }, [filters.startDate, filters.endDate]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredActivities.length / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredActivities.length, page, rowsPerPage]);

  const pagedActivities = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredActivities.slice(start, start + rowsPerPage);
  }, [filteredActivities, page]);

  const handleExportExcel = () => {
    try {
      const pad6 = (row) => {
        const r = [...row];
        while (r.length < 6) r.push('');
        return r;
      };

      const periodFromLabel = filters.startDate ? formatDate(filters.startDate) : 'All dates';
      const periodToLabel = filters.endDate ? formatDate(filters.endDate) : 'All dates';

      const headerSection = [
        pad6(['SUPPLIER LEDGER HISTORY', '', '', '', '', '']),
        pad6(['Supplier name', supplier?.name || '-', '', '', '', '']),
        pad6(['Contact person', supplier?.contactPerson || '-', '', '', '', '']),
        pad6(['Phone', supplier?.phone || '-', '', '', '', '']),
        pad6(['Email', supplier?.email || '-', '', '', '', '']),
        pad6(['Address', supplier?.address || '-', '', '', '', '']),
        pad6(['', '', '', '', '', '']),
        pad6(['REPORT PERIOD (FILTERED)', '', '', '', '', '']),
        pad6(['From', periodFromLabel, '', '', '', '']),
        pad6(['To', periodToLabel, '', '', '', '']),
        pad6(['', '', '', '', '', ''])
      ];

      const transactionHeaders = ['S.No', 'Date', 'Activity', 'Reference', 'Description', 'Amount'];
      const transactionRows = filteredActivities.map((row, index) => [
        index + 1,
        formatDate(row.date),
        row.type,
        row.reference || '-',
        row.description || '-',
        safeNumber(row.amount).toFixed(2)
      ]);

      const finalSheetData = [...headerSection, transactionHeaders, ...transactionRows];
      const worksheet = XLSX.utils.aoa_to_sheet(finalSheetData);
      worksheet['!cols'] = [
        { wch: 10 },
        { wch: 14 },
        { wch: 18 },
        { wch: 22 },
        { wch: 48 },
        { wch: 14 }
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Supplier Ledger');

      XLSX.writeFile(
        workbook,
        `supplier_ledger_history_${supplier?.name || supplierId}_${getTodayIso()}.xlsx`
      );

      enqueueSnackbar('Excel exported successfully', { variant: 'success' });
    } catch (error) {
      console.error('Failed to export excel:', error);
      enqueueSnackbar('Failed to export excel', { variant: 'error' });
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">Supplier Ledger History</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportExcel}
              disabled={filteredActivities.length === 0}
            >
              Export Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/people/suppliers')}
            >
              Back to Suppliers
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>{supplier?.name || 'Supplier'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {supplier?.phone || '-'} {supplier?.email ? `| ${supplier.email}` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {supplier?.address || '-'}
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Start Date"
                    InputLabelProps={{ shrink: true }}
                    value={filters.startDate}
                    onChange={(e) => setFilters((prev) => ({ ...prev, startDate: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="End Date"
                    InputLabelProps={{ shrink: true }}
                    value={filters.endDate}
                    onChange={(e) => setFilters((prev) => ({ ...prev, endDate: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{ height: '56px' }}
                    onClick={() => setFilters({ startDate: getMonthStartIso(), endDate: getTodayIso() })}
                  >
                    This Month
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="text"
                    sx={{ height: '56px' }}
                    onClick={() => setFilters({ startDate: '', endDate: '' })}
                  >
                    Clear Filter
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">Purchases</Typography>
                    <Typography variant="h6">{summary.purchaseCount}</Typography>
                    <Typography variant="body2">{formatCurrency(summary.totalPurchases)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">Returns</Typography>
                    <Typography variant="h6">{summary.returnCount}</Typography>
                    <Typography variant="body2">{formatCurrency(summary.totalReturns)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">Payments</Typography>
                    <Typography variant="h6">{summary.paymentCount}</Typography>
                    <Typography variant="body2">{formatCurrency(summary.totalPayments)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Activity</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredActivities.length > 0 ? (
                      pagedActivities.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{formatDate(row.date)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              icon={
                                row.type === 'PURCHASE'
                                  ? <PurchaseIcon />
                                  : row.type === 'RETURN'
                                    ? <ReturnIcon />
                                    : <PaymentIcon />
                              }
                              label={row.type}
                              color={
                                row.type === 'PURCHASE'
                                  ? 'primary'
                                  : row.type === 'RETURN'
                                    ? 'warning'
                                    : row.type === 'OPENING_BALANCE'
                                      ? 'default'
                                      : 'success'
                              }
                            />
                          </TableCell>
                          <TableCell>{row.reference || '-'}</TableCell>
                          <TableCell>{row.description}</TableCell>
                          <TableCell align="right">{formatCurrency(row.amount)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No activities found in selected date range.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredActivities.length}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10]}
              />
            </Paper>
          </>
        )}
      </Box>
    </Layout>
  );
};

export default SupplierLedgerHistoryPage;
